import { FieldValue, deleteField, increment } from 'firebase/firestore';
import { uniq } from 'lodash';
import uniqid from 'uniqid';
import { Dish, Order, OrderItem, OrderLog, SelfOrderPoint, Store, StoreLog, Worker } from '../../apiTypes';
import { createBatch } from '../../utils/api';
import { concatComponents } from '../../utils/concatComponents';
import { Map } from '../../utils/map';

type Props = {
  cafeSlug: string;
  order: DbDocument<Order>;
  dish?: DbDocument<Dish>;
  orderItem: OrderItem;
  worker?: DbDocument<Worker>;
  selfOrderPointsMap: Map<DbDocument<SelfOrderPoint>>;
};

export const removeItemFromOrder = async ({ order, cafeSlug, dish, worker, orderItem, selfOrderPointsMap }: Props) => {
  if (!dish || !worker || order.paid?.total) {
    return;
  }

  if (
    !worker.credentials.create_and_edit_own_orders &&
    (!worker.credentials.view_orders_of_other_employees || !worker.credentials.edit_orders_of_other_employees)
  ) {
    return;
  }

  const components =
    orderItem.variation && orderItem.variation.components.length > 0
      ? concatComponents(dish.components, orderItem.variation.components)
      : dish.components;

  const selfOrderPoint = order.selfOrderPointId ? selfOrderPointsMap[order.selfOrderPointId] : null;

  const batch = createBatch();
  const storesToUpdate = selfOrderPoint?.storeId ? [selfOrderPoint.storeId] : uniq(components.map((c) => c.store));
  const time = Date.now();

  const orderLog: OrderLog = {
    time: Date.now(),
    workerId: worker.id,
    workerName: worker.name,
    type: 'removeFromOrder',
    dishName: dish.name,
    state: {
      workerId: order.workerId,
      workerName: order.workerName,
      tableId: order.tableId,
      tableName: order.tableName,
      clientId: order.clientId || '',
      clientName: order.clientName || '',
      discount: order.discount || 0,
      items: Object.entries(order.items).reduce<Record<string, OrderItem>>(
        (acc, [id, item]) => (orderItem.id === id ? acc : { ...acc, [id]: item }),
        {}
      ),
      paid: order.paid,
      status: order.status || null,
      delivery: order.delivery || null,
    },
  };

  batch.update<Order>(`cafes/${cafeSlug}/orders/${order?.id}`, {
    [`items.${orderItem.id}`]: deleteField(),
  });

  batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order.id}/logs/${uniqid()}`, orderLog);

  storesToUpdate.forEach((s) => {
    if (s) {
      const diff: Record<string, number> = {};
      const updates: Record<`residues.${string}`, FieldValue> = {};

      components.forEach((c) => {
        if (c.store === s || selfOrderPoint?.storeId) {
          updates[`residues.${c.id}`] = increment(c.qnt);
          diff[c.id] = c.qnt;
        }
      });

      batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

      batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
        time,
        workerId: worker.id,
        workerName: worker.name,
        storeId: s,
        type: 'removeFromOrder',
        diff,
        info: {
          type: 'removeFromOrder',
          number: order.number,
          id: order.id,
          dishName: dish.name,
          dishId: dish.id,
        },
      });
    }
  });

  await batch.commit();
};
